import axios from 'axios';

const BASE = process.env.REACT_APP_API_URL ;

// Use same axios instance as main api — ensures auth token always included
const ax = axios.create({ baseURL: BASE });
ax.interceptors.request.use(c => {
  // Try both common token key names
  const t = localStorage.getItem('accessToken') || localStorage.getItem('token') || sessionStorage.getItem('accessToken');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});
ax.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      // Token expired — redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ── Eligibility ──────────────────────────────────────────────────────────────
export const shaCheckEligibility = (p: { identificationNumber: string; identificationType: string; serviceType?: string; patientId?: string }) =>
  ax.get('/sha/eligibility', { params: p });

export const shaSearchPatient = (identificationNumber: string, identificationType: string) =>
  ax.get('/sha/patients/search', { params: { identificationNumber, identificationType } });

export const shaSearchProfessional = (identificationNumber: string, identificationType: string, regulator = 'KMPDC') =>
  ax.get('/sha/professionals/search', { params: { identificationNumber, identificationType, regulator } });

export const shaSearchFacility = (identifier: string, identifierType: string) =>
  ax.get('/sha/facilities/search', { params: { identifier, 'identifier-type': identifierType } });

export const shaGetFacilityBedOccupancy = (facilityFrCode: string) =>
  ax.get(`/sha/facilities/${facilityFrCode}/beds/occupancy`);

export const shaGetSubBenefits = (shaBeneficiaryCrId: string) =>
  ax.get('/sha/eligibility/sub-benefits', { params: { shaBeneficiaryCrId } });

export const shaGetInterventions = (shaBeneficiaryCrId: string, subBenefitCode: string) =>
  ax.get('/sha/eligibility/interventions', { params: { shaBeneficiaryCrId, subBenefitCode } });

export const shaGetPomsfBalances = (patientId: string, policyYear: string, principalMemberNumber: string) =>
  ax.get('/sha/eligibility/pomsf-balances', { params: { patientId, policyYear, principalMemberNumber } });

export const shaGetBenefitUtilization = (patientId: string, interventionCode: string) =>
  ax.get('/sha/eligibility/utilization', { params: { patientId, interventionCode } });

export const shaAfyaCheck = (identificationNumber: string, identificationType: string) =>
  ax.post('/sha/eligibility/afya-connect', { identificationNumber, identificationType });

// ── Benefits Matrix / Document checklist ────────────────────────────────────
// attachedTypes: DHA document_type codes already attached to this claim (e.g.
// ['DISCHARGE_SUMMARY','FINAL_BILL']) — used to compute satisfied/missing.
// Keyword → DHA intervention code lookup (724-entry Benefits Matrix). Used to suggest an
// interventionCode from a plain clinical service name (e.g. "Hemodialysis", "ANC visit")
// so modules outside IPD/OPD can pre-fill a New Claim link without staff typing a code.
export const shaSearchBenefits = (q: string, limit = 5) =>
  ax.get('/sha/benefits/search', { params: { q, limit } });

export const shaGetRequiredDocuments = (interventionCode: string, attachedTypes: string[] = []) =>
  ax.get(`/sha/benefits/documents-required/${encodeURIComponent(interventionCode)}`, {
    params: attachedTypes.length ? { attached: attachedTypes.join(',') } : {},
  });

// ── OTP / Consent ────────────────────────────────────────────────────────────
export const shaGetPatientContacts = (patientId: string) =>
  ax.get(`/sha/patients/${patientId}/contacts`);

export const shaSendOTP = (patientId: string, interventionCodes: string[], beneficiaryContactId?: string) =>
  ax.post('/sha/otp/send', { patientId, interventionCodes, beneficiaryContactId });

export const shaSendDischargeOTP = (consentToken: string, patientId: string) =>
  ax.post('/sha/otp/discharge', { consentToken, patientId });

export const shaCreateOtpWhitelist = (d: any) =>
  ax.post('/sha/otp-whitelist', d);

export const shaGetOtpWhitelistCallback = (beneficiaryCrId: string, facilityFrCode: string) =>
  ax.get('/sha/otp-whitelist/callback', { params: { beneficiaryCrId, facilityFrCode } });

export const shaAuthorizeOTP = (d: {
  patientId: string; serviceType: string; otp: string; interventions: string[];
  claimId?: string; shaClaimId?: string;
}) =>
  ax.post('/sha/authorize/otp', d);

export const shaAuthorizeBiometrics = (d: any) =>
  ax.post('/sha/authorize/biometrics', d);

export const shaGetAuthorization = (guid: string, beneficiaryCode: string, token: string) =>
  ax.get('/sha/authorizations', { params: { guid, beneficiaryCode, token } });

export const shaRejectAuthorization = (consentToken: string) =>
  ax.post(`/sha/authorizations/${consentToken}/reject`);

export const shaAddNextOfKin = (d: any) =>
  ax.post('/sha/patients/next-of-kin', d);

// ── Claims CRUD ───────────────────────────────────────────────────────────────
export const shaListClaims = (p?: { status?: string; take?: number; skip?: number }) =>
  ax.get('/sha/claims', { params: p });

export const shaGetStats = () =>
  ax.get('/sha/claims/stats');

export const shaGetClaim = (id: string) =>
  ax.get(`/sha/claims/${id}`);

export const shaGetClaimsByAdmission = (admissionId: string) =>
  ax.get(`/sha/claims/by-admission/${admissionId}`);

export const shaGetClaimsByPatient = (patientId: string) =>
  ax.get(`/sha/claims/by-patient/${patientId}`);

export const shaCreateClaim = (d: any) =>
  ax.post('/sha/claims', d);

export const shaUpdateClaim = (id: string, d: any) =>
  ax.put(`/sha/claims/${id}`, d);

// ── Interventions ─────────────────────────────────────────────────────────────
export const shaAddIntervention = (consentToken: string, interventionCode: string, shaClaimId?: string) =>
  ax.post('/sha/interventions/add', { consentToken, interventionCode, shaClaimId });

export const shaSwitchIntervention = (d: { consentToken: string; existingInterventionCode: string; newInterventionCode: string; retainBillItems?: boolean; billFrom?: string; billTo?: string; claimId?: string }) =>
  ax.post('/sha/interventions/switch', d);

export const shaRestoreIntervention = (consentToken: string, interventionCode: string) =>
  ax.post('/sha/interventions/restore', { consentToken, interventionCode });

export const shaRetireIntervention = (consentToken: string, interventionCode: string) =>
  ax.post('/sha/interventions/retire', { consentToken, interventionCode });

// ── Billing ───────────────────────────────────────────────────────────────────
export const shaAddLine = (d: { claimId: string; consentToken: string; interventionCode: string; serviceName: string; serviceIdentifier: string; unitPrice: string; quantity: string; schemeCode: string }) =>
  ax.post('/sha/billing/lines', d);

export const shaRemoveLine = (consentToken: string, lineGuid: string, shaClaimId?: string) =>
  ax.post('/sha/billing/lines/remove', { consentToken, lineGuid, shaClaimId });

export const shaEditLine = (d: { claimLineId: string; quantity: number; schemeCode: string; unitPrice: string; claimId?: string }) =>
  ax.post('/sha/billing/lines/edit', d);

export const shaAddAttachment = (d: any, claimId?: string) =>
  ax.post('/sha/billing/attachments', d);

export const shaRemoveAttachment = (d: { attachmentId: string; consentToken: string; interventionCode: string; shaClaimId?: string }) =>
  ax.post('/sha/billing/attachments/remove', d);

export const shaAddDiagnosis = (consentToken: string, icdCode: string, interventionCode: string, shaClaimId?: string) =>
  ax.post('/sha/billing/diagnoses', { consentToken, icdCode, interventionCode, shaClaimId });

export const shaRemoveDiagnosis = (consentToken: string, icdCode: string, interventionCode: string, shaClaimId?: string) =>
  ax.post('/sha/billing/diagnoses/remove', { consentToken, icdCode, interventionCode, shaClaimId });

export const shaPreviewClaim = (consentToken: string, shaClaimId: string) =>
  ax.post('/sha/billing/preview', { consentToken, shaClaimId });

export const shaPreviewPayerClaim = (providerClaimNo: string) =>
  ax.get('/sha/billing/preview/payer', { params: { providerClaimNo } });

export const shaResubmit = (consentToken: string, shaClaimId: string) =>
  ax.post('/sha/billing/resubmit', { consentToken, shaClaimId });

// ── Dispatch ──────────────────────────────────────────────────────────────────
export const shaDischarge = (d: { claimId: string; consentToken: string; dischargeDate: string; dischargeReason: string; invoiceNumber: string; otp?: string; dischargeAuthGuid?: string; notes?: string }) =>
  ax.post('/sha/discharge', d);

export const shaSubmit = (d: { claimId: string; consentToken: string; invoiceNumber: string; otp?: string; dischargeAuthGuid?: string; dischargeReason?: string; notes?: string; reasonForUnknownPatient?: string }) =>
  ax.post('/sha/submit', d);

export const shaCloseClaim = (d: { claimId: string; consentToken?: string; cancelReasonText: string; cancelReasonType: string }) =>
  ax.post('/sha/close', d);

export const shaRestartClaim = (claimId: string, d?: { asElective?: boolean }) =>
  ax.post(`/sha/claims/${claimId}/restart`, d || {});

// ── Doctor Consent ────────────────────────────────────────────────────────────
export const shaSendDoctorConsent = (d: { identificationNumber: string; requestType: string; consentToken: string; interventionCode: string; identificationType?: string; regulationBody?: string; emergencyClaimId?: string; created?: string }) =>
  ax.post('/sha/doctor-consent', d);

// ── Preauths ──────────────────────────────────────────────────────────────────
export const shaPollPreauth = (consentToken: string, shaClaimId: string) =>
  ax.get('/sha/preauths/poll', { params: { consentToken, shaClaimId } });

export const shaGetPreauthsByClaim = (shaClaimId: string) =>
  ax.get(`/sha/preauths/by-claim/${shaClaimId}`);

export const shaCreatePreauth = (d: any) =>
  ax.post('/sha/preauths', d);

export const shaCancelPreauth = (consentToken: string, interventionCode: string, shaClaimId?: string) =>
  ax.post('/sha/preauths/cancel', { consentToken, interventionCode, shaClaimId });

export const shaRemovePreauthDoctor = (consentToken: string, interventionCode: string, practitionerRegistrationNumber: string) =>
  ax.delete('/sha/preauths/doctors', { data: { consentToken, interventionCode, practitionerRegistrationNumber } });

export const shaRemovePreauthDiagnosis = (icdCode: string, consentToken: string, interventionCode: string) =>
  ax.delete(`/sha/preauths/diagnoses/${icdCode}`, { data: { consentToken, interventionCode } });

// ── ePrescriptions ────────────────────────────────────────────────────────────
export const shaCreatePrescription = (d: any) =>
  ax.post('/sha/prescriptions', d);

export const shaGetPrescriptions = (consentToken: string) =>
  ax.get('/sha/prescriptions', { params: { consentToken } });

export const shaCreateDispense = (d: any) =>
  ax.post('/sha/prescriptions/dispenses', d);

export const shaRemovePrescriptionDoctor = (consentToken: string, interventionCode: string, practitionerRegistrationNumber: string) =>
  ax.delete('/sha/prescriptions/doctors', { data: { consentToken, interventionCode, practitionerRegistrationNumber } });

// ── Emergency ─────────────────────────────────────────────────────────────────
export const shaGetEmergencyProtocols = (interventionCode: string) =>
  ax.get('/sha/emergency/protocols', { params: { interventionCode } });

export const shaAddEmergencyProtocol = (d: { claimId: string; consentToken: string; protocolCode: string; interventionCode: string; unitPrice: number; quantity: number }) =>
  ax.post('/sha/emergency/protocols', d);

export const shaAddEmergencyDoctor = (d: any) =>
  ax.post('/sha/emergency/doctors', d);

export const shaRemoveEmergencyDoctor = (consentToken: string, shaClaimId?: string) =>
  ax.delete('/sha/emergency/doctors', { data: { consentToken, shaClaimId } });

// ── Scenarios ─────────────────────────────────────────────────────────────────
export const shaScenario1CreateVisit = (d: any) => ax.post('/sha/scenarios/1/create-visit', d);
export const shaScenario1AddDiagnoses = (d: any) => ax.post('/sha/scenarios/1/add-diagnoses', d);
export const shaScenario1Preview = (d: any) => ax.post('/sha/scenarios/1/preview', d);
export const shaScenario1Discharge = (d: any) => ax.post('/sha/scenarios/1/discharge', d);
export const shaScenario1SwitchIntervention = (d: any) => ax.post('/sha/scenarios/1/switch-intervention', d);

export const shaScenario2Create = (d: any) => ax.post('/sha/scenarios/2/create', d);
export const shaScenario2PollPreauth = (consentToken: string, shaClaimId: string) => ax.get('/sha/scenarios/2/preauth-status', { params: { consentToken, shaClaimId } });
export const shaScenario2AddBilling = (d: any) => ax.post('/sha/scenarios/2/add-billing', d);
export const shaScenario2Discharge = (d: any) => ax.post('/sha/scenarios/2/discharge', d);

export const shaScenario3PreVisitAuth = (d: any) => ax.post('/sha/scenarios/3/pre-visit-authorize', d);
export const shaScenario3CreatePreauth = (d: any) => ax.post('/sha/scenarios/3/create-preauth', d);
export const shaScenario3PollPreauth = (consentToken: string, shaClaimId: string) => ax.get('/sha/scenarios/3/preauth-status', { params: { consentToken, shaClaimId } });
export const shaScenario3DayOfVisit = (d: any) => ax.post('/sha/scenarios/3/day-of-visit', d);
export const shaScenario3Discharge = (d: any) => ax.post('/sha/scenarios/3/discharge', d);

export const shaScenario4Create = (d: any) => ax.post('/sha/scenarios/4/create', d);
export const shaScenario5Create = (d: any) => ax.post('/sha/scenarios/5/create', d);
export const shaScenario6FullFlow = (d: any) => ax.post('/sha/scenarios/6/full-flow', d);

export const shaScenario7Create = (d: any) => ax.post('/sha/scenarios/7/create', d);
export const shaScenario7AddProtocols = (d: any) => ax.post('/sha/scenarios/7/add-protocols', d);
export const shaScenario7AddDiagnoses = (d: any) => ax.post('/sha/scenarios/7/add-diagnoses', d);
export const shaScenario7Submit = (d: any) => ax.post('/sha/scenarios/7/submit', d);

export const shaScenario8Create = (d: any) => ax.post('/sha/scenarios/8/create', d);
export const shaScenario8IdentifyPatient = (d: any) => ax.post('/sha/scenarios/8/identify-patient', d);
export const shaScenario8Submit = (d: any) => ax.post('/sha/scenarios/8/submit', d);

// ── Terminology ───────────────────────────────────────────────────────────────
export const shaSearchConcepts = (search: string, limit = 10) =>
  ax.get('/sha/terminology/concepts', { params: { search, limit } });

export const shaGetConceptMappings = (fromConcept: string, mapType = 'has-child') =>
  ax.get('/sha/terminology/concepts/mappings', { params: { fromConcept, mapType } });

// ── Audit ─────────────────────────────────────────────────────────────────────
export const shaGetAudit = (p?: { shaClaimId?: string; take?: number }) =>
  ax.get('/sha/audit', { params: p });

// ── Integration / Status ──────────────────────────────────────────────────────
export const shaGetStatusForAdmission = (admissionId: string) =>
  ax.get(`/sha/status/admission/${admissionId}`);

// Generic "encounter" status lookup — visitId is a free-form UUID column on ShaClaim
// (no FK), so this works for any non-admission encounter: OPD visits, and (via
// FileShaClaimButton) dialysis sessions, ANC visits, psych assessments, palliative
// cases, lab/radiology orders — anything that isn't tied to an IPD admission.
export const shaGetStatusForVisit = (visitId: string) =>
  ax.get(`/sha/status/visit/${visitId}`);

export const shaGetBulkStatus = (admissionIds: string[]) =>
  ax.post('/sha/status/bulk', { admissionIds });

export const shaApplyPayment = (claimId: string, approvedAmount: number) =>
  ax.post(`/sha/apply-payment/${claimId}`, { approvedAmount });

// ── Intervention loader for claim page ───────────────────────────────────────
export const shaGetPatientInterventions = (crId: string) =>
  ax.get(`/sha/patients/${crId}/interventions`);

// ── Emergency Claim ────────────────────────────────────────────────────────
export const shaCreateEmergency = (payload: any, claimId?: string) =>
  ax.post('/sha/emergency/create', { ...payload, shaClaimId: claimId });

// ── HMIS Patient Search (our own DB) ──────────────────────────────────────
// Searches patients table by name / UHID / phone / national ID
export const shaSearchHmisPatient = (q: string) =>
  ax.get('/patients/search', { params: { q, limit: 8 } });

// ── Pre-Auth form auto-load ────────────────────────────────────────────────
// This patient's CPOE orders flagged requires_pre_auth=true and still awaiting
// billing, each annotated with whether an approved pre-auth already exists —
// lets the Pre-Auth request form prefill intervention_code/requested_amount
// from a real order instead of the clerk typing them from memory.
export const shaGetPendingPreauthOrders = (patientId: string) =>
  ax.get(`/sha/preauths/pending-for-patient/${patientId}`);